import { useEffect, useMemo, useState } from "react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type {
  AppointmentReminderDataKey,
  Tenant,
  TenantAppointmentReminder,
} from "../../../../types/tenant";
import {
  APP_RESOURCES,
  AccessControl,
} from "../../../../permissions/access-control";
import { WhatsAppPreview } from "../../../../components/whatsapp-template/whatsapp-preview";
import {
  createTemplatePreview,
  getTemplateParameterPositions,
  type WhatsAppTemplate,
} from "../../../../components/whatsapp-template/template-utils";

type TemplateOption = WhatsAppTemplate;

type AppointmentReminderDictionaryItem = {
  key: AppointmentReminderDataKey;
  label: string;
  description: string;
};

type ReminderConfig = Pick<
  Tenant,
  | "appName"
  | "enabledScheduling"
  | "appointmentConfirmation"
  | "appointmentConfirmationTemplateId"
  | "appointmentConfirmationMinutes"
  | "appointmentConfirmationTemplateDataKeys"
  | "appointmentReminderEnabled"
  | "appointmentReminders"
>;

const PAGE_TITLE = "Configurações de agendamento";

type FindAllTenantDto = {
  data: Tenant[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const DEFAULT_REMINDER_MINUTES = 60;
const CONFIRMATION_TEMPLATE_PREFIX = "confirmacao";
const REMINDER_TEMPLATE_PREFIX = "lembrete";

const DEFAULT_DICTIONARY: AppointmentReminderDictionaryItem[] = [
  {
    key: "nome",
    label: "Nome do contato",
    description: "Nome do contato associado ao agendamento.",
  },
  {
    key: "data_hora_agendamento",
    label: "Data e hora do agendamento",
    description: "Data e hora do agendamento no formato local.",
  },
];

const PREVIEW_VALUES: Record<AppointmentReminderDataKey, string> = {
  nome: "[Nome do cliente]",
  data_hora_agendamento: "[20/10/2026 às 14:30]",
};

const createPreviewValues = (
  keys: AppointmentReminderDataKey[],
  positions: number[],
): Record<number, string> =>
  Object.fromEntries(
    keys.map((key, index) => [
      positions[index] ?? index + 1,
      PREVIEW_VALUES[key],
    ]),
  );

const createDefaultReminder = (): TenantAppointmentReminder => ({
  minutes: DEFAULT_REMINDER_MINUTES,
  templateId: "",
  templateDataKeys: [],
});

const normalizeTemplateDataKeys = (
  value: unknown,
): AppointmentReminderDataKey[] => {
  if (!Array.isArray(value)) return [];

  const supported = new Set<AppointmentReminderDataKey>([
    "nome",
    "data_hora_agendamento",
  ]);
  const normalized: AppointmentReminderDataKey[] = [];

  value.forEach((item) => {
    if (typeof item !== "string") return;
    const key = item.trim() as AppointmentReminderDataKey;
    if (!supported.has(key)) return;
    if (normalized.includes(key)) return;
    normalized.push(key);
  });

  return normalized;
};

const normalizeReminderForState = (
  reminder?: TenantAppointmentReminder,
): TenantAppointmentReminder => {
  const minutes = Number(reminder?.minutes);

  return {
    minutes:
      Number.isFinite(minutes) && minutes > 0
        ? minutes
        : DEFAULT_REMINDER_MINUTES,
    templateId:
      typeof reminder?.templateId === "string" ? reminder.templateId : "",
    templateDataKeys: normalizeTemplateDataKeys(reminder?.templateDataKeys),
  };
};

export default function AppointmentReminderConfigPage() {
  const { user } = useAuth();
  const { error, success } = useToast();

  const [tenant, setTenant] = useState<ReminderConfig | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [selectedTenantAppName, setSelectedTenantAppName] = useState("");
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [dictionary, setDictionary] =
    useState<AppointmentReminderDictionaryItem[]>(DEFAULT_DICTIONARY);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const canAccess = useMemo(
    () =>
      AccessControl.canView(
        APP_RESOURCES.CONFIG_APPOINTMENT_REMINDERS,
        user?.role,
      ),
    [user?.role],
  );
  const availableTenantAppNames = useMemo(
    () =>
      Array.from(
        new Set(
          tenants
            .map((item) => item.appName?.trim())
            .filter((appName): appName is string => !!appName),
        ),
      ).sort((left, right) => left.localeCompare(right, "pt-BR")),
    [tenants],
  );
  const confirmationTemplates = useMemo(
    () =>
      templates.filter((template) =>
        template.element_name.startsWith(CONFIRMATION_TEMPLATE_PREFIX),
      ),
    [templates],
  );
  const reminderTemplates = useMemo(
    () =>
      templates.filter((template) =>
        template.element_name.startsWith(REMINDER_TEMPLATE_PREFIX),
      ),
    [templates],
  );

  const resolveTemplateParamLimit = (templateId?: string): number | null => {
    if (!templateId) return null;

    const selectedTemplate = templates.find(
      (template) => template.id === templateId,
    );
    const parsed = Number(selectedTemplate?.params_count);

    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return Math.floor(parsed);
  };

  useEffect(() => {
    if (!canAccess) {
      setTenants([]);
      setSelectedTenantAppName("");
      setIsLoadingTenants(false);
      return;
    }

    let isMounted = true;

    const fetchTenants = async () => {
      try {
        setIsLoadingTenants(true);

        const pageSize = 100;
        let pageNumber = 1;
        let totalPages = 1;
        const allTenants: Tenant[] = [];

        while (pageNumber <= totalPages) {
          const response = await api.get<FindAllTenantDto>("/tenant", {
            params: { page: pageNumber, limit: pageSize },
          });

          allTenants.push(...response.data.data);
          totalPages = response.data.totalPages;
          pageNumber += 1;
        }

        const uniqueTenants = Array.from(
          new Map(allTenants.map((item) => [item.appName, item])).values(),
        );

        if (!isMounted) return;
        setTenants(uniqueTenants);
        setSelectedTenantAppName((previous) => {
          if (
            previous &&
            uniqueTenants.some((item) => item.appName === previous)
          ) {
            return previous;
          }

          if (
            user?.appName &&
            uniqueTenants.some((item) => item.appName === user.appName)
          ) {
            return user.appName;
          }

          return uniqueTenants[0]?.appName ?? "";
        });
      } catch (err) {
        if (!isMounted) return;
        error(getApiErrorMessage(err));
        setTenants([]);
        setSelectedTenantAppName("");
      } finally {
        if (!isMounted) return;
        setIsLoadingTenants(false);
      }
    };

    void fetchTenants();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error, user?.appName]);

  useEffect(() => {
    if (!selectedTenantAppName) {
      setTenant(null);
      setDictionary(DEFAULT_DICTIONARY);
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    const fetchConfig = async () => {
      try {
        setIsLoading(true);
        setTemplates([]);
        setTenant(null);

        const [tenantResponse, dictionaryResponse] = await Promise.all([
          api.get<Tenant>(`/tenant/${selectedTenantAppName}`),
          api
            .get<
              AppointmentReminderDictionaryItem[]
            >("/tenant/appointment-reminder-data-dictionary")
            .catch(() => null),
        ]);

        const data = tenantResponse.data;
        const loadedReminders = Array.isArray(data.appointmentReminders)
          ? data.appointmentReminders.map((item) =>
              normalizeReminderForState(item),
            )
          : [];

        setTenant({
          appName: data.appName,
          enabledScheduling: data.enabledScheduling,
          appointmentConfirmation: !!data.appointmentConfirmation,
          appointmentConfirmationTemplateId:
            data.appointmentConfirmationTemplateId ?? "",
          appointmentConfirmationMinutes:
            data.appointmentConfirmationMinutes ?? DEFAULT_REMINDER_MINUTES,
          appointmentConfirmationTemplateDataKeys: normalizeTemplateDataKeys(
            data.appointmentConfirmationTemplateDataKeys,
          ),
          appointmentReminderEnabled: !!data.appointmentReminderEnabled,
          appointmentReminders:
            data.appointmentReminderEnabled && loadedReminders.length === 0
              ? [createDefaultReminder()]
              : loadedReminders,
        });

        if (
          Array.isArray(dictionaryResponse?.data) &&
          dictionaryResponse.data.length > 0
        ) {
          const filteredDictionary = dictionaryResponse.data.filter(
            (item): item is AppointmentReminderDictionaryItem =>
              item?.key === "nome" || item?.key === "data_hora_agendamento",
          );
          setDictionary(
            filteredDictionary.length > 0
              ? filteredDictionary
              : DEFAULT_DICTIONARY,
          );
        } else {
          setDictionary(DEFAULT_DICTIONARY);
        }
      } catch (err) {
        error(getApiErrorMessage(err));
        setTenant(null);
        setDictionary(DEFAULT_DICTIONARY);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchConfig();
  }, [selectedTenantAppName, error]);

  useEffect(() => {
    if (!tenant?.appName || !tenant.enabledScheduling) {
      setTemplates([]);
      return;
    }

    let cancelled = false;

    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await api.get<TemplateOption[]>(
          `/templates/${tenant.appName}`,
        );

        if (!cancelled) {
          setTemplates(response.data ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setTemplates([]);
          error(getApiErrorMessage(err));
        }
      } finally {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, [tenant?.appName, tenant?.enabledScheduling, error]);

  const updateReminder = (
    index: number,
    updates: Partial<TenantAppointmentReminder>,
  ) => {
    setTenant((previous) => {
      if (!previous) return previous;

      const currentReminders = [...(previous.appointmentReminders ?? [])];
      const current = normalizeReminderForState(currentReminders[index]);
      const next: TenantAppointmentReminder = {
        ...current,
        ...updates,
      };

      if (typeof updates.templateId === "string") {
        const limit = resolveTemplateParamLimit(updates.templateId);
        if (limit !== null) {
          next.templateDataKeys = normalizeTemplateDataKeys(
            next.templateDataKeys,
          ).slice(0, limit);
        }
      }

      currentReminders[index] = next;

      return {
        ...previous,
        appointmentReminders: currentReminders,
      };
    });
  };

  const addReminder = () => {
    setTenant((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        appointmentReminders: [
          ...(previous.appointmentReminders ?? []),
          createDefaultReminder(),
        ],
      };
    });
  };

  const removeReminder = (index: number) => {
    setTenant((previous) => {
      if (!previous) return previous;

      const reminders = previous.appointmentReminders ?? [];
      if (reminders.length <= 1) return previous;

      return {
        ...previous,
        appointmentReminders: reminders.filter(
          (_, itemIndex) => itemIndex !== index,
        ),
      };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenant?.appName || isSaving) return;

    const remindersToSend = (tenant.appointmentReminders ?? []).map((item) =>
      normalizeReminderForState(item),
    );

    if (
      tenant.appointmentConfirmation &&
      !tenant.appointmentConfirmationTemplateId
    ) {
      error("Selecione o template de confirmação da consulta.");
      return;
    }

    if (
      tenant.appointmentConfirmation &&
      (!Number.isFinite(tenant.appointmentConfirmationMinutes) ||
        Number(tenant.appointmentConfirmationMinutes) <= 0)
    ) {
      error("Informe minutos válidos para a confirmação da consulta.");
      return;
    }

    if (tenant.appointmentConfirmation) {
      const confirmationParamLimit = resolveTemplateParamLimit(
        tenant.appointmentConfirmationTemplateId,
      );
      if (confirmationParamLimit === null) {
        error(
          "Não foi possível validar a quantidade de parâmetros do template de confirmação.",
        );
        return;
      }
      const selectedConfirmationParams = normalizeTemplateDataKeys(
        tenant.appointmentConfirmationTemplateDataKeys,
      ).length;
      if (selectedConfirmationParams !== confirmationParamLimit) {
        error(
          `A confirmação deve preencher exatamente ${confirmationParamLimit} parâmetro(s) do template. Atualmente: ${selectedConfirmationParams}.`,
        );
        return;
      }
    }

    if (tenant.appointmentReminderEnabled && remindersToSend.length === 0) {
      error("Adicione ao menos um lembrete quando ele estiver habilitado.");
      return;
    }

    for (
      let index = 0;
      tenant.appointmentReminderEnabled && index < remindersToSend.length;
      index += 1
    ) {
      const reminder = remindersToSend[index];

      if (!Number.isFinite(reminder.minutes) || reminder.minutes <= 0) {
        error(`Informe minutos válidos no lembrete ${index + 1}.`);
        return;
      }

      if (!reminder.templateId || reminder.templateId.trim().length === 0) {
        error(`Selecione o template do lembrete ${index + 1}.`);
        return;
      }

      const templateParamLimit = resolveTemplateParamLimit(reminder.templateId);
      if (templateParamLimit === null) {
        error(
          `Não foi possível validar a quantidade de parâmetros do template no lembrete ${index + 1}.`,
        );
        return;
      }

      const selectedDataKeysCount = normalizeTemplateDataKeys(
        reminder.templateDataKeys,
      ).length;

      if (selectedDataKeysCount !== templateParamLimit) {
        error(
          `O lembrete ${index + 1} deve preencher exatamente ${templateParamLimit} parâmetro(s) do template. Atualmente: ${selectedDataKeysCount}.`,
        );
        return;
      }
    }

    try {
      setIsSaving(true);
      await api.patch(`/tenant/${tenant.appName}`, {
        appointmentConfirmation: !!tenant.appointmentConfirmation,
        appointmentConfirmationTemplateId: tenant.appointmentConfirmation
          ? tenant.appointmentConfirmationTemplateId || undefined
          : undefined,
        appointmentConfirmationMinutes: tenant.appointmentConfirmationMinutes,
        appointmentConfirmationTemplateDataKeys:
          tenant.appointmentConfirmationTemplateDataKeys ?? [],
        appointmentReminderEnabled: !!tenant.appointmentReminderEnabled,
        appointmentReminders: tenant.appointmentReminderEnabled
          ? remindersToSend
          : [],
      });
      success("Configurações de agendamento atualizadas com sucesso!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const tenantSelector = (
    <div className="max-w-md">
      <label className="form-label">Tenant</label>
      <select
        className="form-input"
        value={selectedTenantAppName}
        onChange={(event) => setSelectedTenantAppName(event.target.value)}
        disabled={isLoadingTenants || isLoading || isSaving}
      >
        {isLoadingTenants && <option value="">Carregando...</option>}
        {!isLoadingTenants && availableTenantAppNames.length === 0 && (
          <option value="">Nenhum tenant disponível</option>
        )}
        {!isLoadingTenants &&
          availableTenantAppNames.map((appName) => (
            <option key={appName} value={appName}>
              {appName}
            </option>
          ))}
      </select>
    </div>
  );

  if (!canAccess) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-2">{PAGE_TITLE}</h1>
        <p className="app-page-subtitle">
          Você não tem permissão para acessar esta página.
        </p>
      </div>
    );
  }

  if (isLoadingTenants || isLoading) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">{PAGE_TITLE}</h1>
        {tenantSelector}
        <p className="app-page-subtitle">Carregando configurações...</p>
      </div>
    );
  }

  if (!selectedTenantAppName) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">{PAGE_TITLE}</h1>
        {tenantSelector}
        <p className="app-page-subtitle mt-4">
          Nenhum tenant disponível para edição.
        </p>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">{PAGE_TITLE}</h1>
        {tenantSelector}
        <p className="app-page-subtitle">
          Não foi possível carregar as configurações do tenant.
        </p>
      </div>
    );
  }

  if (!tenant.enabledScheduling) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">{PAGE_TITLE}</h1>
        {tenantSelector}
        <p className="app-page-subtitle">
          O agendamento está desabilitado para este tenant.
        </p>
      </div>
    );
  }

  const confirmationTemplateDataKeys = normalizeTemplateDataKeys(
    tenant.appointmentConfirmationTemplateDataKeys,
  );
  const confirmationTemplateParamLimit = resolveTemplateParamLimit(
    tenant.appointmentConfirmationTemplateId,
  );
  const confirmationTemplate = templates.find(
    (template) => template.id === tenant.appointmentConfirmationTemplateId,
  );
  const confirmationPositions =
    getTemplateParameterPositions(confirmationTemplate);
  const confirmationPreview = createTemplatePreview(
    confirmationTemplate,
    createPreviewValues(confirmationTemplateDataKeys, confirmationPositions),
  );

  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title mb-6">{PAGE_TITLE}</h1>
      {tenantSelector}

      <form onSubmit={handleSubmit} className="space-y-6 mt-6">
        <section className="app-card space-y-4 p-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!tenant.appointmentConfirmation}
              onChange={(event) =>
                setTenant((previous) =>
                  previous
                    ? {
                        ...previous,
                        appointmentConfirmation: event.target.checked,
                      }
                    : previous,
                )
              }
              className="form-checkbox"
            />
            <span className="text-sm font-medium text-slate-700">
              Enviar confirmação de consulta
            </span>
          </label>

          {tenant.appointmentConfirmation && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-4">
                <div>
                  <label className="form-label">Disparo antes (min)</label>
                  <input
                    type="number"
                    min={1}
                    value={
                      tenant.appointmentConfirmationMinutes ??
                      DEFAULT_REMINDER_MINUTES
                    }
                    onChange={(event) => {
                      const parsed = Number(event.target.value);
                      setTenant((previous) =>
                        previous
                          ? {
                              ...previous,
                              appointmentConfirmationMinutes:
                                Number.isFinite(parsed) && parsed > 0
                                  ? parsed
                                  : DEFAULT_REMINDER_MINUTES,
                            }
                          : previous,
                      );
                    }}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Template de confirmação</label>
                  <select
                    value={tenant.appointmentConfirmationTemplateId ?? ""}
                    onChange={(event) => {
                      const templateId = event.target.value;
                      setTenant((previous) =>
                        previous
                          ? {
                              ...previous,
                              appointmentConfirmationTemplateId:
                                templateId || undefined,
                              appointmentConfirmationTemplateDataKeys: [],
                            }
                          : previous,
                      );
                    }}
                    disabled={templatesLoading}
                    className="form-input disabled:bg-slate-100"
                  >
                    <option value="">
                      {templatesLoading
                        ? "Carregando templates..."
                        : "Selecione um template"}
                    </option>
                    {confirmationTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.element_name}
                      </option>
                    ))}
                  </select>
                  {!templatesLoading && confirmationTemplates.length === 0 && (
                    <p className="mt-1 text-xs text-slate-500">
                      Nenhum template aprovado encontrado.
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  <p className="form-label mb-0">Parâmetros do template</p>
                  {confirmationPositions.map((position, parameterIndex) => (
                    <label className="block" key={position}>
                      <span className="form-label text-xs">
                        Parâmetro {`{{${position}}}`}
                      </span>
                      <select
                        className="form-input"
                        disabled={
                          parameterIndex > confirmationTemplateDataKeys.length
                        }
                        value={
                          confirmationTemplateDataKeys[parameterIndex] ?? ""
                        }
                        onChange={(event) => {
                          const next = [...confirmationTemplateDataKeys];
                          if (event.target.value) {
                            next[parameterIndex] = event.target
                              .value as AppointmentReminderDataKey;
                          } else {
                            next.splice(parameterIndex, 1);
                          }
                          setTenant((previous) =>
                            previous
                              ? {
                                  ...previous,
                                  appointmentConfirmationTemplateDataKeys: next,
                                }
                              : previous,
                          );
                        }}
                      >
                        <option value="">
                          Selecione um dado do agendamento
                        </option>
                        {dictionary.map((item) => (
                          <option
                            key={item.key}
                            value={item.key}
                            disabled={confirmationTemplateDataKeys.some(
                              (key, index) =>
                                key === item.key && index !== parameterIndex,
                            )}
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                  {confirmationTemplate &&
                    confirmationPositions.length === 0 && (
                      <p className="rounded-lg border bg-slate-50 p-3 text-xs text-slate-500">
                        Este template não possui parâmetros.
                      </p>
                    )}
                </div>
                {confirmationTemplateParamLimit !== null && (
                  <p className="mt-1 text-xs text-slate-500">
                    Preenchidos {confirmationTemplateDataKeys.length} de{" "}
                    {confirmationTemplateParamLimit} parâmetros.
                  </p>
                )}
              </div>
              <WhatsAppPreview
                appName={tenant.appName}
                ctaLabels={confirmationPreview.ctaLabels}
                message={confirmationPreview.message}
              />
            </div>
          )}
        </section>

        <section className="app-card space-y-4 p-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!tenant.appointmentReminderEnabled}
              onChange={(event) =>
                setTenant((previous) => {
                  if (!previous) return previous;
                  const enabled = event.target.checked;
                  return {
                    ...previous,
                    appointmentReminderEnabled: enabled,
                    appointmentReminders:
                      enabled &&
                      (previous.appointmentReminders ?? []).length === 0
                        ? [createDefaultReminder()]
                        : previous.appointmentReminders,
                  };
                })
              }
              className="form-checkbox"
            />
            <span className="text-sm font-medium text-slate-700">
              Enviar lembretes de consulta
            </span>
          </label>

          {tenant.appointmentReminderEnabled && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-800">
                  Itens
                </h2>
                <button
                  type="button"
                  onClick={addReminder}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                >
                  Adicionar lembrete
                </button>
              </div>

              {templatesLoading && (
                <p className="text-xs text-slate-500">
                  Carregando templates...
                </p>
              )}

              {!templatesLoading &&
                (tenant.appointmentReminders ?? []).map((reminder, index) => {
                  const current = normalizeReminderForState(reminder);
                  const selectedTemplateDataKeys = normalizeTemplateDataKeys(
                    current.templateDataKeys,
                  );
                  const templateParamLimit = resolveTemplateParamLimit(
                    current.templateId,
                  );
                  const selectedTemplate = templates.find(
                    (template) => template.id === current.templateId,
                  );
                  const parameterPositions =
                    getTemplateParameterPositions(selectedTemplate);
                  const reminderPreview = createTemplatePreview(
                    selectedTemplate,
                    createPreviewValues(
                      selectedTemplateDataKeys,
                      parameterPositions,
                    ),
                  );

                  return (
                    <div
                      key={index}
                      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-800">
                          Lembrete {index + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() => removeReminder(index)}
                          disabled={
                            (tenant.appointmentReminders ?? []).length <= 1
                          }
                          className="text-xs font-medium text-rose-600 disabled:cursor-not-allowed disabled:text-slate-400"
                        >
                          Remover
                        </button>
                      </div>

                      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
                        <div className="space-y-4">
                          <div>
                            <label className="form-label">
                              Disparo antes (min)
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={current.minutes}
                              onChange={(event) => {
                                const parsed = Number(event.target.value);
                                updateReminder(index, {
                                  minutes:
                                    Number.isFinite(parsed) && parsed > 0
                                      ? parsed
                                      : DEFAULT_REMINDER_MINUTES,
                                });
                              }}
                              className="form-input"
                            />
                          </div>

                          <div>
                            <label className="form-label">Template</label>
                            <select
                              value={current.templateId ?? ""}
                              onChange={(event) =>
                                updateReminder(index, {
                                  templateId: event.target.value,
                                  templateDataKeys: [],
                                })
                              }
                              className="form-input"
                              disabled={templatesLoading || !tenant.appName}
                            >
                              <option value="">
                                {templatesLoading
                                  ? "Carregando templates..."
                                  : "Selecione um template"}
                              </option>
                              {reminderTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.element_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="space-y-3">
                            <p className="form-label mb-0">
                              Parâmetros do template
                            </p>
                            {parameterPositions.map(
                              (position, parameterIndex) => (
                                <label className="block" key={position}>
                                  <span className="form-label text-xs">
                                    Parâmetro {`{{${position}}}`}
                                  </span>
                                  <select
                                    className="form-input"
                                    disabled={
                                      parameterIndex >
                                      selectedTemplateDataKeys.length
                                    }
                                    value={
                                      selectedTemplateDataKeys[
                                        parameterIndex
                                      ] ?? ""
                                    }
                                    onChange={(event) => {
                                      const next = [
                                        ...selectedTemplateDataKeys,
                                      ];
                                      if (event.target.value) {
                                        next[parameterIndex] = event.target
                                          .value as AppointmentReminderDataKey;
                                      } else {
                                        next.splice(parameterIndex, 1);
                                      }
                                      updateReminder(index, {
                                        templateDataKeys: next,
                                      });
                                    }}
                                  >
                                    <option value="">
                                      Selecione um dado do agendamento
                                    </option>
                                    {dictionary.map((item) => (
                                      <option
                                        key={item.key}
                                        value={item.key}
                                        disabled={selectedTemplateDataKeys.some(
                                          (key, itemIndex) =>
                                            key === item.key &&
                                            itemIndex !== parameterIndex,
                                        )}
                                      >
                                        {item.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              ),
                            )}
                            {selectedTemplate &&
                              parameterPositions.length === 0 && (
                                <p className="rounded-lg border bg-slate-50 p-3 text-xs text-slate-500">
                                  Este template não possui parâmetros.
                                </p>
                              )}
                          </div>
                          {templateParamLimit !== null && (
                            <p className="mt-1 text-xs text-slate-500">
                              Preenchidos {selectedTemplateDataKeys.length} de{" "}
                              {templateParamLimit} parâmetros.
                            </p>
                          )}
                        </div>
                        <WhatsAppPreview
                          appName={tenant.appName}
                          ctaLabels={reminderPreview.ctaLabels}
                          message={reminderPreview.message}
                        />
                      </div>
                    </div>
                  );
                })}

              <p className="text-xs text-slate-500">
                O disparo é calculado em minutos de antecedência ao horário da
                consulta.
              </p>
            </div>
          )}
        </section>

        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={isSaving}
          loadingLabel="Salvando"
        >
          Salvar alterações
        </LoadingButton>
      </form>
    </div>
  );
}
